/**
 * Lease Detail Page
 * Shows lease details with tabs: Overview, Payments, Transactions, Documents.
 * Mobile-first: prioritized information hierarchy.
 */

import { useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Pencil, ClipboardCheck, ChevronRight, FileText, Check, Link2, TrendingUp } from 'lucide-react';
import {
  getPaymentConfig,
  createPaymentConfig,
  updatePaymentConfig,
  listChargeConfigs,
  createChargeConfig,
  updateChargeConfig,
  deleteChargeConfig,
  listLandlordFundingSources,
  getLease,
  updateLease,
  updateProperty,
  getLeaseBalance,
  archiveLease,
  unarchiveLease,
  setTenantDepartureDate,
  scheduleMoveOut,
  addTenantToLease,
  listTenantsByUnit,
  type PaymentConfig,
  type UpdatePaymentConfigInput,
  type LeaseChargeConfig,
  type CreateChargeConfigInput,
  type UpdateChargeConfigInput,
  type LandlordFundingSource,
  type PropertyBankInfo,
  type PaymentReadiness,
  type TenantWithDetails,
  setPropertyBankAccount,
  ApiClientError,
  fetchPendingLateFees,
  approvePendingLateFee,
  dismissPendingLateFee,
  type PendingLateFee,
  getWalkthroughForLease,
  type WalkthroughSummaryResponse,
  type WalkthroughPhase,
  getDeductionReport,
  type DeductionReportResponse,
  issueTenantAccessToken,
  type IssueTenantAccessTokenResponse,
  listLeaseDocuments,
  listRentIncreasesForLease,
  type RentIncreaseResponse,
} from '../lib/api';
import { fetchLinkedBankAccounts, type LinkedBankAccount } from '../api/linked-bank-accounts.api';
import {
  fetchLeaseMatchingSummary,
  confirmExternalPaymentMatch,
  dismissExternalPaymentTransaction,
  type LeaseMatchingSummary,
} from '../api/external-payments-hub.api';
import { PendingMatchBanner } from '../components/external-payments/PendingMatchBanner';
import { CurrentPeriodPaymentCard } from '../components/external-payments/CurrentPeriodPaymentCard';
import type {
  LeaseDetail,
  UpdateLeaseInput,
  ArchiveLeaseInput,
  LeaseValidationWarning,
  ScheduleMoveOutInput,
  ArchiveLeaseResult,
  LeaseState,
} from '../types/lease';
import { ArchiveLeaseModal } from '../components/ArchiveLeaseModal';
import { LeaseDocumentSection } from '../components/lease/LeaseDocumentSection';
import { LeaseAmendmentsSection } from '../components/lease/LeaseAmendmentsSection';
import { DocumentsSection } from '../components/documents/DocumentsSection';
import { Modal } from '../components/Modal';
import { LeaseForm } from '../components/LeaseForm';
import { Badge } from '../components/ui/badge';
import { PageHeader, PageSection, DataGrid, DataRow, StatusBadge, ListItem } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { DetailTabs, TabsContent } from '../components/layout';
import { TenantChecklistBanner } from '../components/condition/TenantChecklistBanner';
import { LeaseChecklistsTab } from '../components/condition/LeaseChecklistsTab';
import { TenantChecklistModal } from '../components/condition/TenantChecklistModal';
import { useChecklists } from '../lib/useChecklists';

// Payment sub-components
import { deriveInitialMethodIntent, type MethodSelection } from '../components/payments/payment-helpers';
import { RecurringChargesSection } from '../components/payments/RecurringChargesSection';
import { PaymentsSetupChecklist } from '../components/payments/PaymentsSetupChecklist';
import { PaymentsStatusPanel } from '../components/payments/PaymentsStatusPanel';
import { LeaseLedgerTab } from '../components/payments/LeaseLedgerTab';
import { MatchingActivitySection } from '../components/payments/MatchingActivitySection';
import { formatLocalDate, formatCalendarDate, formatTimestamp, deriveTimezone } from '../utils/date';
import { todayInTimezone, asCalendarDateString, asIsoDateTimeString } from '@resida/shared';
import { PropertyTimezoneProvider } from '../contexts/PropertyTimezoneContext';
import { useUnitTimezone } from '../hooks/useUnitTimezone';
import { getLeaseCapabilities } from '../utils/lease-capabilities';
import { ensureWorkflow, listWorkflows } from '../lib/workflow.api';

/* ── Helpers ── */

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * lease_end saga step progress (BR-LL-08/09/10 — "an ending lease stays fully visible with
 * its wind-down progress on show"). The saga TRACKS, never ACTS, so this is read-only display.
 *
 * `types/workflow.ts`'s WorkflowListItem union does not yet include the 'lease_end' saga
 * type (that file is outside this layer's edit scope for this slice) — read structurally
 * here instead of widening that shared union.
 */
interface LeaseEndProgressStep {
  label: string;
  satisfied: boolean;
}

interface RawWorkflowItem {
  type?: string;
  lineageType?: string;
  lineageId?: string;
  members?: Array<{ preconditions?: Array<{ label: string; satisfied: boolean }> }>;
}

function extractLeaseEndSteps(workflows: unknown[], leaseId: string): LeaseEndProgressStep[] | null {
  const match = (workflows as RawWorkflowItem[]).find(
    (w) => w.type === 'lease_end' && w.lineageType === 'lease' && w.lineageId === leaseId
  );
  const preconditions = match?.members?.[0]?.preconditions;
  if (!preconditions || preconditions.length === 0) return null;
  return preconditions.map((p) => ({ label: p.label, satisfied: p.satisfied }));
}


/* ── Component ── */

export function LeaseDetailPage() {
  const { leaseId } = useParams<{ leaseId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Workflow CTAs deep-link with ?focus=<section> so the payments tab scrolls to and highlights
  // the section the landlord needs to act on (deposit / terms / late-fees / monitoring / activate).
  const focusSection = searchParams.get('focus');
  // Once the focus has been consumed (scrolled to + highlighted), strip ?focus= from the URL —
  // preserving the #payments tab hash. This is what makes RE-pressing the same CTA work: react-router
  // treats navigating to an identical URL as a no-op, so without clearing it the second press never
  // changes searchParams and the scroll effect never re-fires (the "same-page nav does nothing" bug).
  const clearFocus = useCallback(() => {
    if (!leaseId) return;
    const next = new URLSearchParams(searchParams);
    next.delete('focus');
    const qs = next.toString();
    navigate(`/leases/${leaseId}${qs ? `?${qs}` : ''}#payments`, { replace: true });
  }, [leaseId, searchParams, navigate]);
  const queryClient = useQueryClient();

  const [lease, setLease] = useState<LeaseDetail | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  // Sourced by unit ID via the batched cache (issue #910) — not from lease.unit.state,
  // which requires whoever built the payload to have remembered to select it.
  const timezoneQuery = useUnitTimezone(unitId);
  const propertyTimezone = timezoneQuery.data ?? deriveTimezone('');
  const [paymentConfig, setPaymentConfig] = useState<PaymentConfig | null>(null);
  // BR-21: whether this lease has a SIGNED generated document (drives the payment-setup banner).
  const [hasSignedLeaseDocument, setHasSignedLeaseDocument] = useState(false);
  const [chargeConfigs, setChargeConfigs] = useState<LeaseChargeConfig[]>([]);
  const [fundingSources, setFundingSources] = useState<LandlordFundingSource[]>([]);
  const [propertyBankInfo, setPropertyBankInfo] = useState<PropertyBankInfo | null>(null);
  const [readiness, setReadiness] = useState<PaymentReadiness>({ ach: { ready: false, issues: [] }, external: { ready: false, issues: [] } });
  const [linkedBankAccounts, setLinkedBankAccounts] = useState<LinkedBankAccount[]>([]);
  const [balance, setBalance] = useState<number | null>(null);

  const [matchingSummary, setMatchingSummary] = useState<LeaseMatchingSummary | null>(null);
  const [pendingLateFees, setPendingLateFees] = useState<PendingLateFee[]>([]);
  const [isApprovingFee, setIsApprovingFee] = useState<string | null>(null);
  const [isDismissingFee, setIsDismissingFee] = useState<string | null>(null);
  const [lateFeeError, setLateFeeError] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  // Non-fatal lookup: only wizard-originated leases have a generated document (BR-21).
  const wizardLeaseId = lease?.source === 'wizard' ? lease.id : null;
  useEffect(() => {
    if (!wizardLeaseId) {
      setHasSignedLeaseDocument(false);
      return;
    }
    let cancelled = false;
    listLeaseDocuments(wizardLeaseId)
      .then(result => {
        if (!cancelled) setHasSignedLeaseDocument(result.documents.some(d => d.status === 'SIGNED'));
      })
      .catch(() => {
        if (!cancelled) setHasSignedLeaseDocument(false);
      });
    return () => {
      cancelled = true;
    };
  }, [wizardLeaseId]);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Edit lease modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSavingLease, setIsSavingLease] = useState(false);
  const [leaseWarnings, setLeaseWarnings] = useState<LeaseValidationWarning[]>([]);
  const [leaseSubmitError, setLeaseSubmitError] = useState<string | null>(null);

  // Archive modal state
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  // Un-archive modal state
  const [isUnarchiveModalOpen, setIsUnarchiveModalOpen] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);

  // Existing deduction report for this lease (BR-20a/DC-8), fetched read-only via
  // getDeductionReport(leaseId) — null when none has ever been compiled. Drives the
  // archived-lease "Condition Records" section's Deduction Report leg the same way
  // moveIn/moveOutWalkthroughSummary drive the other two legs: a "(record)" link when a
  // report exists, otherwise a non-interactive "not recorded" placeholder. The lookup
  // never creates a report, so simply viewing an archived lease can't manufacture state.
  const [deductionReportSummary, setDeductionReportSummary] = useState<DeductionReportResponse | null>(null);

  // Departure date modal state
  const [departureTenant, setDepartureTenant] = useState<{ id: string; firstName: string; lastName: string } | null>(null);
  const [departureDate, setDepartureDate] = useState<string>('');
  const [isDepartureSaving, setIsDepartureSaving] = useState(false);
  const [departureError, setDepartureError] = useState<string | null>(null);

  // Inline edit lease terms state (BYO leases only)
  const [editingField, setEditingField] = useState<'rent' | 'deposit' | 'startDate' | 'endDate' | null>(null);
  const [editFieldValue, setEditFieldValue] = useState('');
  const [isSavingField, setIsSavingField] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  // Recurring charges modal state
  const [isChargesModalOpen, setIsChargesModalOpen] = useState(false);

  // Add tenant modal state (BYO leases only)
  const [isAddTenantModalOpen, setIsAddTenantModalOpen] = useState(false);
  const [availableTenantsToAdd, setAvailableTenantsToAdd] = useState<TenantWithDetails[]>([]);
  const [selectedAddTenantId, setSelectedAddTenantId] = useState<string>('');
  const [isLoadingAvailableTenants, setIsLoadingAvailableTenants] = useState(false);
  const [isAddingTenant, setIsAddingTenant] = useState(false);
  const [addTenantError, setAddTenantError] = useState<string | null>(null);

  // Schedule Move-Out modal state (BR-LL-08)
  const [isScheduleMoveOutModalOpen, setIsScheduleMoveOutModalOpen] = useState(false);
  const [scheduleMoveOutDate, setScheduleMoveOutDate] = useState('');
  const [scheduleMoveOutReason, setScheduleMoveOutReason] = useState('');
  const [isSchedulingMoveOut, setIsSchedulingMoveOut] = useState(false);
  const [scheduleMoveOutError, setScheduleMoveOutError] = useState<string | null>(null);

  // Share Tenant Access Link modal state (BR-8a/8b)
  const [isShareLinkModalOpen, setIsShareLinkModalOpen] = useState(false);
  const [shareLinkResult, setShareLinkResult] = useState<IssueTenantAccessTokenResponse | null>(null);
  const [isIssuingShareLink, setIsIssuingShareLink] = useState(false);
  const [shareLinkError, setShareLinkError] = useState<string | null>(null);
  const [isShareLinkCopied, setIsShareLinkCopied] = useState(false);
  // Separate from shareLinkError: a clipboard-write failure is not a failed token issue and
  // must not surface the Retry button (which re-issues a brand-new token).
  const [shareLinkCopyError, setShareLinkCopyError] = useState<string | null>(null);

  // Archive outstanding-balance warning (ADR-LL-020) — warns, never blocks
  const [isArchiveWarningOpen, setIsArchiveWarningOpen] = useState(false);

  // lease_end saga step progress, shown while the lease is 'ending' or 'ended'
  const [leaseEndSteps, setLeaseEndSteps] = useState<LeaseEndProgressStep[] | null>(null);

  // Landlord's own move-in condition walkthrough summary (BR-20) — drives the "Done"
  // indicator on the Move-In Baseline entry point.
  const [moveInWalkthroughSummary, setMoveInWalkthroughSummary] = useState<WalkthroughSummaryResponse | null>(null);
  // Landlord's own move-out condition walkthrough summary (BR-20) — used (alongside
  // moveInWalkthroughSummary above) to gate the archived-lease "Condition Records"
  // links on whether a walkthrough was actually ever created, instead of rendering a
  // "(record)" link unconditionally (see the archived-section render below).
  const [moveOutWalkthroughSummary, setMoveOutWalkthroughSummary] = useState<WalkthroughSummaryResponse | null>(null);
  // Tenant checklist status (BR-34): ONE lease-scoped DC-12 request feeds the Overview banner and the
  // Checklists tab — never per-phase lookups, never createWalkthrough.
  const checklists = useChecklists({ leaseId }, { enabled: !!leaseId });
  const [viewingTenantChecklist, setViewingTenantChecklist] = useState<{ walkthroughId: string; phase: WalkthroughPhase } | null>(null);
  const handleViewTenantChecklist = useCallback(
    (walkthroughId: string, phase: WalkthroughPhase) => setViewingTenantChecklist({ walkthroughId, phase }),
    [],
  );

  // Rent Increase status card (lease-lifecycle — The Rent Increase Process, J-RI-INITIATE):
  // the current non-terminal (DRAFT/ACTIVE) RentIncrease for this lease, if any. The
  // partial-unique-index constraint (rent_increases_one_in_flight_per_lease) guarantees
  // at most one such record exists at a time.
  const [rentIncreaseCard, setRentIncreaseCard] = useState<RentIncreaseResponse | null>(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaseId]);

  async function loadData() {
    if (!leaseId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [leaseResponse, configResponse, chargesResponse, sourcesResponse, balanceResponse, linkedBankAccountsResponse, moveInWalkthroughResponse, moveOutWalkthroughResponse, deductionReportResponse, rentIncreasesResponse] =
        await Promise.all([
          getLease('', leaseId) as Promise<unknown>,
          getPaymentConfig(leaseId),
          listChargeConfigs(leaseId),
          listLandlordFundingSources(),
          getLeaseBalance(leaseId).catch(() => ({ balance: 0 })),
          fetchLinkedBankAccounts().catch(() => ({ accounts: [] as LinkedBankAccount[] })),
          getWalkthroughForLease(leaseId, 'MOVE_IN', 'LANDLORD').catch(() => ({ walkthrough: null })),
          getWalkthroughForLease(leaseId, 'MOVE_OUT', 'LANDLORD').catch(() => ({ walkthrough: null })),
          getDeductionReport(leaseId).catch(() => ({ report: null })),
          listRentIncreasesForLease(leaseId).catch(() => ({ rentIncreases: [] as RentIncreaseResponse[] })),
        ]);

      const leaseData = leaseResponse as LeaseDetail;
      setLease(leaseData);
      setUnitId(leaseData.unitId);
      setBalance(balanceResponse.balance);
      setMoveInWalkthroughSummary(moveInWalkthroughResponse.walkthrough);
      setMoveOutWalkthroughSummary(moveOutWalkthroughResponse.walkthrough);
      setDeductionReportSummary(deductionReportResponse.report);
      setRentIncreaseCard(
        rentIncreasesResponse.rentIncreases.find((ri) => ri.status === 'DRAFT' || ri.status === 'ACTIVE') ?? null
      );

      // Saga progress is only relevant once a move-out is scheduled (or the lease has
      // wound down naturally) — skip the extra request otherwise.
      if (leaseData.state === 'ending' || leaseData.state === 'ended') {
        void loadLeaseEndProgress(leaseData.id);
      } else {
        setLeaseEndSteps(null);
      }

      let pc = configResponse.paymentConfig;
      // BR-LL-11: an archived lease is read-only — the API rejects every write, including this
      // lazy first-time config creation, so don't attempt it (it would surface as a console error).
      if (!pc && leaseData.state !== 'archived') {
        pc = await createPaymentConfig(leaseId);
      }

      setPaymentConfig(pc);
      setPropertyBankInfo(configResponse.propertyBankInfo ?? null);
      setReadiness(configResponse.readiness);
      setChargeConfigs(chargesResponse.chargeConfigs);
      setFundingSources(sourcesResponse.fundingSources);
      setLinkedBankAccounts(linkedBankAccountsResponse.accounts);

      // Fetch matching summary whenever a payment config exists, regardless of mode: the
      // service layer (GET /landlord/api/leases/:id/external-payment-status) computes pending
      // suggested matches for every paymentMethod ('none'/'ach'/'external') — AC-11 ("returns
      // ALL pending suggested matches for the lease") and AC-13 ("remains present on ALL tabs")
      // carry no mode restriction. Gating this fetch to 'external' only left ACH-mode leases
      // with real pending matches never surfacing the banner (change-19-ui-review.md, Critical
      // Finding #1). Pending late fees remain scoped to external-mode leases — that gate isn't
      // part of this finding and stays as-is.
      if (pc) {
        fetchLeaseMatchingSummary(leaseId).then(setMatchingSummary).catch(console.error);
      }
      if (pc && pc.paymentMethod === 'external') {
        fetchPendingLateFees(leaseId).then(r => setPendingLateFees(r.pendingLateFees)).catch(console.error);
      }
    } catch (err) {
      setError('Failed to load lease configuration');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  // Fetches the lease_end saga's step progress for this lease, if one exists. Non-fatal —
  // the saga is display-only (it TRACKS, never ACTS), so a failure here must not block the
  // rest of the page. Called fire-and-forget right after scheduling a move-out (see
  // handleScheduleMoveOut below). Fetches cancelled by a page navigation never reach this
  // catch — see installUnloadFetchGuard (@resida/shared), installed in main.tsx.
  async function loadLeaseEndProgress(id: string) {
    try {
      const { workflows } = await listWorkflows();
      setLeaseEndSteps(extractLeaseEndSteps(workflows, id));
    } catch (err) {
      console.error('Failed to load lease-end saga progress', err);
    }
  }

  async function handleApprovePendingLateFee(id: string) {
    if (!leaseId) return;
    setIsApprovingFee(id);
    setLateFeeError(null);
    try {
      await approvePendingLateFee(id);
      const result = await fetchPendingLateFees(leaseId);
      setPendingLateFees(result.pendingLateFees);
    } catch (err) {
      setLateFeeError(err instanceof Error ? err.message : 'Failed to approve late fee');
    } finally {
      setIsApprovingFee(null);
    }
  }

  async function handleDismissPendingLateFee(id: string) {
    if (!leaseId) return;
    setIsDismissingFee(id);
    setLateFeeError(null);
    try {
      await dismissPendingLateFee(id);
      const result = await fetchPendingLateFees(leaseId);
      setPendingLateFees(result.pendingLateFees);
    } catch (err) {
      setLateFeeError(err instanceof Error ? err.message : 'Failed to dismiss late fee');
    } finally {
      setIsDismissingFee(null);
    }
  }

  /**
   * Persists the user's payment method selection as `methodIntent` on the
   * payment config. This does NOT activate payments (paymentMethod is not
   * touched). The intent is used by `deriveInitialMethodIntent` so the
   * selection survives a page reload and can be read by workflow branches.
   *
   * Maps UI MethodSelection → methodIntent:
   *   'ach'      → 'ach'
   *   'external' → 'external'
   *   'none'     → 'skipped'
   *
   * Returns true when the config persisted, false otherwise — mirroring handleUpdateConfig's
   * convention. The caller (PaymentSettingsTab.handleMethodChange) uses this to decide whether to
   * roll back its optimistic selectedMethod update: a silently-swallowed failure would leave the
   * selector showing a method the server never saved (AC-10).
   */
  async function handleMethodIntentChange(method: MethodSelection): Promise<boolean> {
    if (!leaseId) return false;
    const methodIntent: 'ach' | 'external' | 'skipped' =
      method === 'none' ? 'skipped' : method;
    try {
      const config = paymentConfig
        ? await updatePaymentConfig(leaseId, { methodIntent })
        : await createPaymentConfig(leaseId, { methodIntent });
      setPaymentConfig(config);

      // Choosing a real method (ACH/External) is the TRIGGER for the payment-setup workflow:
      // ensure one exists for this lease (idempotent — dedupes to the move-in saga's payment
      // member if active, or creates a standalone one, e.g. after a prior "skip"). Choosing
      // "Not Configured" → 'skipped' just records the decision, which completes any active one.
      if (method !== 'none' && unitId) {
        await ensureWorkflow({
          type: 'payment_setup',
          dedupeKey: `payment_setup:${leaseId}`,
          lineageType: 'lease',
          lineageId: leaseId,
          subjectType: 'unit',
          subjectId: unitId,
          triggerSource: 'lease_payments_method_select',
        });
      }

      // Live-update the workflow rail: choosing a method reactivates the payment_setup member.
      // The create/update call above already returned the authoritative config (set above), so we
      // deliberately do NOT re-fetch here — a redundant GET would race the in-progress payment form
      // and reset fields the user is editing.
      await queryClient.invalidateQueries({ queryKey: ['workflows'] });
      return true;
    } catch (err) {
      console.error('Failed to persist methodIntent', err);
      setError('Failed to save payment method. Please try again.');
      return false;
    }
  }

  // Returns true when the config persisted, false otherwise. Callers (the payment cards) use this
  // to decide whether to clear their unsaved-changes affordance — clearing optimistically would
  // hide the Save button even when the PUT failed, making a user believe a failed save succeeded.
  async function handleUpdateConfig(data: UpdatePaymentConfigInput): Promise<boolean> {
    if (!leaseId) return false;

    setError(null);
    setSuccessMessage(null);

    // Step 1: the PUT IS the persistence. Its success/failure is the save's success/failure —
    // nothing after it may downgrade the boolean we return to the card.
    let config: PaymentConfig;
    try {
      config = await updatePaymentConfig(leaseId, data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to update payment settings');
      }
      console.error(err);
      return false;
    }

    // Step 2: the save is durable. The readiness re-fetch and rail invalidation below are
    // best-effort UI refresh — a failure here must NOT make the card believe the save failed
    // (that would keep the "unsaved changes" Save button up after a successful save).
    setPaymentConfig(config);
    setSuccessMessage('Payment settings updated successfully');
    setTimeout(() => setSuccessMessage(null), 3000);
    // Fire the readiness re-fetch without awaiting — it's best-effort. Awaiting it would
    // block `return true`, keeping isSubmitting=true and the Save button visible until the
    // GET resolves, which violates the "must NOT block the card" contract above.
    getPaymentConfig(leaseId).then(refreshed => {
      setReadiness(refreshed.readiness);
    }).catch(err => {
      console.error('Readiness re-fetch failed after a successful save (non-fatal)', err);
    });
    // Live-update the workflow panel: a config change may satisfy a payment_setup step
    // (deposit / terms / late fees / activation). Without this the rail only reflects the
    // change after a manual page refresh.
    void queryClient.invalidateQueries({ queryKey: ['workflows'] });
    return true;
  }


  async function handleConfirmMatch(transactionId: string) {
    await confirmExternalPaymentMatch(transactionId);
    if (leaseId) {
      fetchLeaseMatchingSummary(leaseId).then(setMatchingSummary).catch(console.error);
    }
  }

  async function handleDismissMatch(transactionId: string) {
    await dismissExternalPaymentTransaction(transactionId);
    if (leaseId) {
      fetchLeaseMatchingSummary(leaseId).then(setMatchingSummary).catch(console.error);
    }
  }


  async function handleSetPropertyDefault(fundingSourceId: string | null) {
    if (!propertyBankInfo) return;

    try {
      setError(null);
      setSuccessMessage(null);
      await updateProperty(propertyBankInfo.propertyId, {
        defaultDepositAccountId: fundingSourceId,
      });
      if (fundingSourceId) {
        const source = fundingSources.find(s => s.id === fundingSourceId);
        if (source) {
          setPropertyBankInfo({
            ...propertyBankInfo,
            defaultDepositAccount: {
              id: source.id,
              bankName: source.bankName,
              accountMask: source.accountMask,
              friendlyName: source.friendlyName ?? null,
              isPrimary: source.isPrimary,
            },
          });
        }
      } else {
        setPropertyBankInfo({
          ...propertyBankInfo,
          defaultDepositAccount: null,
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setSuccessMessage('Property default deposit account updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to update property default');
      }
      console.error(err);
    }
  }

  async function handleSetMonitoringAccount(linkedBankAccountId: string) {
    if (!propertyBankInfo) return;

    try {
      setError(null);
      setSuccessMessage(null);
      await setPropertyBankAccount(propertyBankInfo.propertyId, linkedBankAccountId);
      const account = linkedBankAccounts.find(a => a.id === linkedBankAccountId);
      if (account) {
        setPropertyBankInfo({
          ...propertyBankInfo,
          monitoringAccount: {
            linkedBankAccountId: account.id,
            institutionName: account.institutionName,
            accountName: account.accountName,
            accountMask: account.accountMask,
            status: account.status,
          },
        });
      }
      // Re-fetch readiness and propertyBankInfo since monitoring account changed
      if (leaseId) {
        const refreshed = await getPaymentConfig(leaseId);
        setReadiness(refreshed.readiness);
        if (refreshed.propertyBankInfo) {
          setPropertyBankInfo(refreshed.propertyBankInfo);
        }
      }
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      setSuccessMessage('Monitoring account updated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to set monitoring account');
      }
      console.error(err);
    }
  }

  async function handleCreateCharge(data: CreateChargeConfigInput) {
    if (!leaseId) return;

    try {
      setError(null);
      setSuccessMessage(null);
      const chargeConfig = await createChargeConfig(leaseId, data);
      setChargeConfigs([...chargeConfigs, chargeConfig]);
      setSuccessMessage('Charge added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to add charge');
      }
      console.error(err);
      throw err;
    }
  }

  async function handleUpdateCharge(chargeConfigId: string, data: UpdateChargeConfigInput) {
    if (!leaseId) return;

    try {
      setError(null);
      setSuccessMessage(null);
      const chargeConfig = await updateChargeConfig(leaseId, chargeConfigId, data);
      setChargeConfigs(chargeConfigs.map(c => c.id === chargeConfigId ? chargeConfig : c));
      setSuccessMessage('Charge updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to update charge');
      }
      console.error(err);
      throw err;
    }
  }

  async function handleDeleteCharge(chargeConfigId: string) {
    if (!leaseId) return;

    try {
      setError(null);
      setSuccessMessage(null);
      await deleteChargeConfig(leaseId, chargeConfigId);
      setChargeConfigs(chargeConfigs.filter(c => c.id !== chargeConfigId));
      setSuccessMessage('Charge deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to delete charge');
      }
      console.error(err);
    }
  }

  async function handleEditLease(data: UpdateLeaseInput, acknowledgeWarnings: boolean) {
    if (!leaseId || !unitId) return;

    try {
      setIsSavingLease(true);
      setLeaseSubmitError(null);
      setLeaseWarnings([]);

      const payload = acknowledgeWarnings ? { ...data, acknowledgeWarnings: true } : data;
      const result = await updateLease(unitId, leaseId, payload);

      const resultAny = result as unknown as Record<string, unknown>;
      if ('warnings' in resultAny && !acknowledgeWarnings) {
        setLeaseWarnings((resultAny.warnings as LeaseValidationWarning[]) ?? []);
        return;
      }

      setLease(result as unknown as LeaseDetail);
      setIsEditModalOpen(false);
      setSuccessMessage('Lease updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setLeaseSubmitError(err.message);
      } else {
        setLeaseSubmitError('Failed to update lease');
      }
      console.error(err);
    } finally {
      setIsSavingLease(false);
    }
  }

  async function handleArchive(data: ArchiveLeaseInput) {
    if (!leaseId || !lease) return;
    try {
      setIsArchiving(true);
      setArchiveError(null);
      // ArchiveLeaseResponseSchema is the flat lease object extended with settlement
      // context (ADR-LL-020) — not wrapped in `{ lease }`.
      const result: ArchiveLeaseResult = await archiveLease(lease.unitId, leaseId, data);
      setLease(result);
      setIsArchiveModalOpen(false);
      setIsArchiveWarningOpen(false);
      setSuccessMessage(
        result.isSettled
          ? 'Lease archived successfully'
          : `Lease archived — ${formatCurrency(result.outstandingBalanceCents)} still outstanding`
      );
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setArchiveError(err instanceof Error ? err.message : 'Failed to archive lease');
    } finally {
      setIsArchiving(false);
    }
  }

  async function handleUnarchive() {
    if (!leaseId) return;
    try {
      setIsUnarchiving(true);
      setError(null);
      // LeaseResponseSchema is the flat lease object — not wrapped in `{ lease }`.
      const result = await unarchiveLease(leaseId);
      setLease(result);
      setIsUnarchiveModalOpen(false);
      setSuccessMessage('Lease un-archived successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message);
      } else {
        setError('Failed to un-archive lease');
      }
      setIsUnarchiveModalOpen(false);
      console.error(err);
    } finally {
      setIsUnarchiving(false);
    }
  }

  /** Opens the pre-archive step: an outstanding-balance warning (ADR-LL-020) if there is one,
   *  otherwise the archive modal directly. WARNS, never blocks. */
  function handleArchiveClick() {
    if ((balance ?? 0) > 0) {
      setIsArchiveWarningOpen(true);
    } else {
      setIsArchiveModalOpen(true);
    }
  }

  function openDepartureModal(tenant: { id: string; firstName: string; lastName: string }) {
    const today = todayInTimezone(propertyTimezone);
    setDepartureTenant(tenant);
    setDepartureDate(today);
    setDepartureError(null);
  }

  function closeDepartureModal() {
    setDepartureTenant(null);
    setDepartureDate('');
    setDepartureError(null);
  }

  async function handleSetDeparture() {
    if (!leaseId || !departureTenant) return;
    try {
      setIsDepartureSaving(true);
      setDepartureError(null);
      // LeaseResponseSchema is the flat lease object — not wrapped in `{ lease }`.
      const result = await setTenantDepartureDate(leaseId, departureTenant.id, departureDate);
      setLease(result);
      closeDepartureModal();
      setSuccessMessage('Departure date set successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'LAST_TENANT') {
        setDepartureError('A lease must have at least one active tenant.');
      } else if (err instanceof ApiClientError) {
        setDepartureError(err.message);
      } else {
        setDepartureError('Failed to set departure date');
      }
      console.error(err);
    } finally {
      setIsDepartureSaving(false);
    }
  }

  /* ── Schedule Move-Out (BR-LL-08) ── */

  function openScheduleMoveOutModal() {
    setScheduleMoveOutDate(lease?.endDate ? lease.endDate.substring(0, 10) : todayInTimezone(propertyTimezone));
    setScheduleMoveOutReason('');
    setScheduleMoveOutError(null);
    setIsScheduleMoveOutModalOpen(true);
  }

  function closeScheduleMoveOutModal() {
    setIsScheduleMoveOutModalOpen(false);
    setScheduleMoveOutError(null);
  }

  /* ── Share Tenant Access Link (BR-8a/8b) ── */

  async function handleShareLinkClick() {
    if (!leaseId) return;
    setIsShareLinkModalOpen(true);
    setShareLinkError(null);
    setShareLinkResult(null);
    setIsShareLinkCopied(false);
    setShareLinkCopyError(null);
    setIsIssuingShareLink(true);
    try {
      const result = await issueTenantAccessToken(leaseId);
      setShareLinkResult(result);
    } catch (err) {
      setShareLinkError(err instanceof Error ? err.message : 'Failed to issue tenant access link.');
    } finally {
      setIsIssuingShareLink(false);
    }
  }

  function closeShareLinkModal() {
    setIsShareLinkModalOpen(false);
    setShareLinkResult(null);
    setShareLinkError(null);
    setIsShareLinkCopied(false);
    setShareLinkCopyError(null);
  }

  async function handleCopyShareLink() {
    if (!shareLinkResult) return;
    setShareLinkCopyError(null);
    try {
      await navigator.clipboard.writeText(shareLinkResult.magicLinkUrl);
    } catch (err) {
      setShareLinkCopyError(err instanceof Error ? err.message : 'Failed to copy the link — copy it manually instead.');
      return;
    }
    setIsShareLinkCopied(true);
    window.setTimeout(() => setIsShareLinkCopied(false), 2000);
  }

  async function handleScheduleMoveOut() {
    if (!leaseId || !scheduleMoveOutDate) return;
    try {
      setIsSchedulingMoveOut(true);
      setScheduleMoveOutError(null);
      const input: ScheduleMoveOutInput = {
        endDate: scheduleMoveOutDate,
        reason: scheduleMoveOutReason.trim() || null,
      };
      const result = await scheduleMoveOut(leaseId, input);
      setLease(result);
      closeScheduleMoveOutModal();
      setSuccessMessage('Move-out scheduled successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      // Scheduling spawns (or re-plans) the lease_end saga — refresh both the global
      // workflow rail and this page's inline step progress.
      void queryClient.invalidateQueries({ queryKey: ['workflows'] });
      void loadLeaseEndProgress(leaseId);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setScheduleMoveOutError(err.message);
      } else {
        setScheduleMoveOutError('Failed to schedule move-out');
      }
      console.error(err);
    } finally {
      setIsSchedulingMoveOut(false);
    }
  }

  function startEditField(field: 'rent' | 'deposit' | 'startDate' | 'endDate') {
    if (!lease) return;
    if (field === 'rent') {
      setEditFieldValue((lease.rentAmount / 100).toFixed(2));
    } else if (field === 'deposit') {
      setEditFieldValue((lease.securityDeposit / 100).toFixed(2));
    } else if (field === 'startDate') {
      setEditFieldValue(lease.startDate.substring(0, 10));
    } else {
      setEditFieldValue(lease.endDate?.substring(0, 10) ?? '');
    }
    setFieldError(null);
    setEditingField(field);
  }

  function cancelEditField() {
    setEditingField(null);
    setFieldError(null);
  }

  async function handleSaveField() {
    if (!leaseId || !unitId || !editingField) return;
    try {
      setIsSavingField(true);
      setFieldError(null);

      let update: UpdateLeaseInput;
      let label: string;

      if (editingField === 'rent' || editingField === 'deposit') {
        const cents = Math.round(parseFloat(editFieldValue) * 100);
        if (isNaN(cents) || cents < 0) {
          setFieldError('Please enter a valid amount');
          return;
        }
        update = editingField === 'rent' ? { rentAmount: cents } : { securityDeposit: cents };
        label = editingField === 'rent' ? 'Monthly rent' : 'Security deposit';
      } else if (editingField === 'startDate') {
        if (!editFieldValue) {
          setFieldError('Start date is required');
          return;
        }
        update = { startDate: editFieldValue };
        label = 'Start date';
      } else {
        // endDate — empty string means clear to month-to-month
        update = { endDate: editFieldValue || null };
        label = 'End date';
      }

      const result = await updateLease(unitId, leaseId, update);
      const updated = (result as unknown as Record<string, unknown>);
      const updatedLease = (updated.lease ?? result) as unknown as LeaseDetail;
      setLease(updatedLease);
      setEditingField(null);
      setSuccessMessage(`${label} updated`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFieldError(err.message);
      } else {
        setFieldError('Failed to update');
      }
    } finally {
      setIsSavingField(false);
    }
  }

  async function openAddTenantModal() {
    if (!lease?.unitId) return;
    setAddTenantError(null);
    setSelectedAddTenantId('');
    setIsAddTenantModalOpen(true);
    try {
      setIsLoadingAvailableTenants(true);
      const result = await listTenantsByUnit(lease.unitId);
      const currentTenantIds = new Set(lease.tenants.map(t => t.id));
      setAvailableTenantsToAdd(result.tenants.filter(t => !currentTenantIds.has(t.id)));
    } catch {
      setAddTenantError('Failed to load tenants');
    } finally {
      setIsLoadingAvailableTenants(false);
    }
  }

  function closeAddTenantModal() {
    setIsAddTenantModalOpen(false);
    setAddTenantError(null);
    setSelectedAddTenantId('');
  }

  async function handleAddTenant() {
    if (!leaseId || !selectedAddTenantId) return;
    try {
      setIsAddingTenant(true);
      setAddTenantError(null);
      const result = await addTenantToLease(leaseId, selectedAddTenantId);
      const addResult = (result as unknown as Record<string, unknown>);
      const updatedLease = (addResult.lease ?? result) as unknown as LeaseDetail;
      setLease(updatedLease);
      closeAddTenantModal();
      setSuccessMessage('Tenant added successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setAddTenantError(err.message);
      } else {
        setAddTenantError('Failed to add tenant');
      }
    } finally {
      setIsAddingTenant(false);
    }
  }

  if (isLoading) {
    return <LeaseDetailSkeleton />;
  }

  const cap = getLeaseCapabilities(lease?.state, lease?.source, paymentConfig?.paymentMethod ?? 'none');
  const recurringTotal = chargeConfigs.reduce((sum, c) => sum + c.amount, 0);

  return (
    <PropertyTimezoneProvider timezone={propertyTimezone}>
    <div className="max-w-4xl mx-auto pb-6" data-testid="lease-detail-page">
      {/* Header */}
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Lease"
          backLink={unitId ? { to: `/units/${unitId}`, label: 'Back to Unit' } : undefined}
          action={
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {lease && (
                <div className="inline-flex items-center gap-1.5">
                  <StatusBadge status={lease.state} data-testid="lease-state-badge" />
                  {lease.state === 'ending' && lease.endDate && (
                    <Badge
                      variant="warning"
                      className="whitespace-nowrap"
                      data-testid="lease-ending-badge"
                    >
                      {/* "Ending" is dropped on narrow viewports — the adjacent state badge already
                          says "Ending"; repeating it here overflowed the action row off-screen on
                          mobile (this badge + Schedule Move-Out pushed the row past 358px on a
                          390px viewport). */}
                      <span className="hidden sm:inline">Ending </span>
                      {formatCalendarDate(asCalendarDateString(lease.endDate))}
                    </Badge>
                  )}
                </div>
              )}
              {cap.canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditModalOpen(true)}
                  data-testid="edit-lease-button"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              )}
              {cap.canScheduleMoveOut && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openScheduleMoveOutModal}
                  data-testid="schedule-move-out-btn"
                >
                  <span className="hidden sm:inline">Schedule </span>Move-Out
                </Button>
              )}
              {lease && lease.state !== 'archived' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleArchiveClick}
                  data-testid="archive-lease-btn"
                  className="text-destructive hover:text-destructive"
                >
                  Archive
                </Button>
              )}
              {lease?.state === 'archived' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsUnarchiveModalOpen(true)}
                  data-testid="unarchive-lease-btn"
                >
                  Un-archive
                </Button>
              )}
            </div>
          }
        />
      </div>

      {/* Pending Match Banner — shown on all tabs */}
      {matchingSummary && matchingSummary.pendingMatches.length > 0 && (
        <div className="mx-[var(--page-padding-x)] mb-3">
          <PendingMatchBanner
            matches={matchingSummary.pendingMatches}
            totalCount={matchingSummary.pendingMatchCount}
            onConfirm={handleConfirmMatch}
            onDismiss={handleDismissMatch}
          />
        </div>
      )}

      {/* Messages */}
      {error && (
        <div
          data-testid="error-message"
          role="alert"
          aria-live="polite"
          className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm"
        >
          <p className="text-destructive m-0">{error}</p>
        </div>
      )}

      {successMessage && (
        <div
          data-testid="success-message"
          role="alert"
          aria-live="polite"
          className="mx-[var(--page-padding-x)] mb-4 rounded-lg border border-success/30 bg-success/5 p-3 text-sm"
        >
          <p className="text-success m-0">{successMessage}</p>
        </div>
      )}

      {/* BR-21: a signed wizard lease with no payment collection says so and links to payment settings. */}
      {hasSignedLeaseDocument && (!paymentConfig || paymentConfig.paymentMethod === 'none') && (
        <div
          data-testid="payment-setup-banner"
          role="status"
          className="mx-[var(--page-padding-x)] mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm"
        >
          <p className="m-0 font-medium text-foreground">Payment collection isn&apos;t set up</p>
          {/* Plain hash anchor: fires `hashchange`, which DetailTabs listens to (router pushState would not). */}
          <a
            href="#payments"
            data-testid="payment-setup-banner-link"
            className="inline-flex min-h-[44px] items-center text-sm font-medium text-primary underline"
          >
            Set up payment collection
          </a>
        </div>
      )}

      {/* Tabs */}
      <DetailTabs
        tabs={[
          { key: 'overview', label: 'Overview', testId: 'overview-tab' },
          { key: 'payments', label: 'Payments', testId: 'payments-tab' },
          ...(cap.showTransactions ? [{ key: 'transactions' as const, label: 'Transactions', testId: 'transactions-tab' }] : []),
          { key: 'documents', label: 'Documents', testId: 'documents-tab' },
          { key: 'checklists', label: 'Checklists', testId: 'checklists-tab' },
        ]}
        defaultTab="overview"
      >

        {/* ── Overview Tab ── */}
        <TabsContent value="overview" className="mt-0">
          {lease && (
            <>
              <TenantChecklistBanner
                checklists={checklists}
                lease={{ state: lease.state, startDate: lease.startDate }}
                onView={handleViewTenantChecklist}
              />

              {/* Move-In Baseline (BR-20): elevated entry point into the move-in condition
                  walkthrough while the tenancy hasn't started or is underway. Shows a "Done"
                  indicator once the landlord's own move-in walkthrough has been submitted
                  (any non-DRAFT status) — the entry point stays clickable so it can also be
                  used to review/correct an already-submitted walkthrough. */}
              {(lease.state === 'scheduled' || lease.state === 'active') && (
                <div className="mx-[var(--page-padding-x)] md:mx-4 mb-4">
                  <button
                    type="button"
                    onClick={() => navigate(`/leases/${leaseId}/condition-walkthroughs/move-in`)}
                    className="w-full flex items-center justify-between gap-2 p-4 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors min-h-[44px] text-left"
                    data-testid="lease-condition-walkthrough-move-in-link"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ClipboardCheck className="h-4 w-4 text-primary shrink-0" />
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-foreground block truncate">Move-In Baseline</span>
                        <span className="text-xs text-muted-foreground block truncate">
                          Record the unit&apos;s condition before the tenant moves in
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {moveInWalkthroughSummary && moveInWalkthroughSummary.status !== 'DRAFT' && (
                        <span
                          className="inline-flex items-center gap-1 text-xs font-medium text-success"
                          data-testid="lease-move-in-baseline-done"
                        >
                          <Check className="h-4 w-4" aria-hidden="true" /> Done
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </button>
                </div>
              )}

              {/* Rent Increase status card (lease-lifecycle — The Rent Increase Process,
                  J-RI-INITIATE/J-RI-SERVICE): shows the current non-terminal rent increase's
                  next actionable step, or (when none exists, and the lease is still active
                  enough to start one) an entry point into the initiation form. Without this
                  entry point NewRentIncreasePage would be unreachable from the UI — added
                  beyond the compiled plan's literal card-only description to keep the route
                  navigable; flagged in the layer report. */}
              {rentIncreaseCard ? (
                (() => {
                  const currentStep =
                    rentIncreaseCard.steps.find((s) => s.status === 'PENDING' || s.status === 'OVERDUE') ??
                    rentIncreaseCard.steps[0] ??
                    null;
                  return (
                    <div className="mx-[var(--page-padding-x)] md:mx-4 mb-4">
                      <button
                        type="button"
                        onClick={() => navigate(`/rent-increases/${rentIncreaseCard.id}`)}
                        className="w-full flex items-center justify-between gap-2 p-4 rounded-lg border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors min-h-[44px] text-left"
                        data-testid="lease-rent-increase-card"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <TrendingUp className="h-4 w-4 text-primary shrink-0" />
                          <div className="min-w-0">
                            <span className="text-sm font-medium text-foreground block truncate">
                              Rent Increase — {rentIncreaseCard.status}
                            </span>
                            <span className="text-xs text-muted-foreground block truncate">
                              {currentStep
                                ? `${currentStep.label} · due ${formatCalendarDate(asCalendarDateString(currentStep.dueDate))} · ${currentStep.status}`
                                : 'View details'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0 text-xs font-medium text-primary">
                          View Details
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        </div>
                      </button>
                    </div>
                  );
                })()
              ) : (
                (lease.state === 'scheduled' || lease.state === 'active') && (
                  <div className="mx-[var(--page-padding-x)] md:mx-4 mb-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/leases/${leaseId}/rent-increases/new`)}
                      className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                      data-testid="lease-start-rent-increase-link"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <TrendingUp className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">Start Rent Increase</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  </div>
                )
              )}

              {/* Move-Out Progress — the lease_end saga's step progress (when a saga actually
                  exists — `lease-end-saga-progress` stays scoped to JUST that content, per
                  lease-lifecycle's own BR-LL-08/09/13 regression guard, which asserts this
                  testid is absent whenever no lease_end saga was spawned, e.g. a fixed-term
                  lease's natural expiry), plus the Move-Out Walkthrough entry point (BR-20).
                  The outer section (and the Move-Out Walkthrough entry within it) is shown
                  while the lease is winding down ('ending') or has wound down but not yet
                  archived ('ended') REGARDLESS of whether a saga was ever spawned — BR-20 scopes
                  that entry point to lifecycle state alone, never to saga existence — so the
                  lease stays fully visible the whole time. */}
              {(lease.state === 'ending' || lease.state === 'ended') && (
                <PageSection title="Move-Out Progress">
                  {leaseEndSteps && leaseEndSteps.length > 0 && (
                    <div data-testid="lease-end-saga-progress">
                      <ul
                        className="flex flex-col gap-2 px-[var(--page-padding-x)] md:px-4 py-2 m-0 list-none"
                        data-testid="lease-end-saga-steps"
                      >
                        {leaseEndSteps.map((step, i) => (
                          <li
                            key={i}
                            className="flex items-center gap-2 text-sm"
                            data-testid={`lease-end-step-${i}`}
                          >
                            <span aria-hidden="true" className={step.satisfied ? 'text-success' : 'text-muted-foreground'}>
                              {step.satisfied ? '✓' : '○'}
                            </span>
                            <span className={step.satisfied ? 'text-foreground' : 'text-muted-foreground'}>
                              {step.label}
                              <span className="sr-only">{step.satisfied ? ' — completed' : ' — pending'}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="px-[var(--page-padding-x)] md:px-4 pb-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/leases/${leaseId}/condition-walkthroughs/move-out`)}
                      className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                      data-testid="lease-condition-walkthrough-move-out-link"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">Move-Out Walkthrough</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  </div>
                </PageSection>
              )}

              {/* Share Tenant Access Link — the landlord-side action for the already-spec'd
                  magic-link issue touchpoint (§7). One shared action, not one per phase: the
                  tenant-side phase resolver (change-1) determines MOVE_IN vs MOVE_OUT from
                  lease state, not from the token. Visible whenever the lease isn't archived. */}
              {lease.state !== 'archived' && (
                <div className="mx-[var(--page-padding-x)] md:mx-4 mb-4">
                  <button
                    type="button"
                    onClick={handleShareLinkClick}
                    className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                    data-testid="share-tenant-access-link-button"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium text-foreground truncate">Share tenant access link</span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  </button>
                </div>
              )}

              {/* Settlement — the 'ended' window: tenancy is over, but settlement continues
                  (BR-LL-10). Manual charges and deposit disposition remain available until
                  the lease is archived. Also hosts the Deduction Report entry point (BR-20). */}
              {lease.state === 'ended' && (
                <PageSection title="Settlement" data-testid="lease-settlement-section">
                  <DataGrid columns={1}>
                    <DataRow
                      label="Outstanding Balance"
                      value={balance != null ? formatCurrency(balance) : '—'}
                    />
                  </DataGrid>
                  <p
                    className="px-[var(--page-padding-x)] md:px-4 text-sm text-muted-foreground m-0"
                    data-testid="lease-settlement-note"
                  >
                    Tenancy has ended. Manual charges and deposit disposition remain available in
                    the Transactions tab until this lease is archived.
                  </p>
                  <div className="px-[var(--page-padding-x)] md:px-4 pt-3 pb-3">
                    <button
                      type="button"
                      onClick={() => navigate(`/leases/${leaseId}/deduction-report`)}
                      className="w-full flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                      data-testid="lease-deduction-report-link"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">Deduction Report</span>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  </div>
                </PageSection>
              )}

              {/* Condition Records (BR-20): the lifecycle sections above stop rendering once
                  a lease is archived, so the walkthrough/report entry points move here as
                  read-only record links. All three legs are gated uniformly on actual existence
                  (moveIn/moveOutWalkthroughSummary and deductionReportSummary, all fetched above)
                  rather than rendering unconditionally — an archived lease that never had a
                  walkthrough run or report compiled shows a plainly-labeled "Not recorded" row
                  instead of a misleading "(record)" link that would silently spawn a fresh DRAFT
                  walkthrough or compile a report on click (BR-20 is presentation-only; it must
                  never manufacture new state). The deduction-report leg uses the read-only
                  `getDeductionReport(leaseId)` lookup (BR-20a/DC-8), which never creates a row, so
                  it can be gated the same way as the two walkthrough links. */}
              {lease.state === 'archived' && (
                <PageSection title="Condition Records" data-testid="lease-condition-records-section">
                  <div className="flex flex-col gap-2 px-[var(--page-padding-x)] md:px-4 pb-3">
                    {moveInWalkthroughSummary ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/leases/${leaseId}/condition-walkthroughs/move-in`)}
                        className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                        data-testid="lease-condition-walkthrough-move-in-link"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium text-foreground truncate">Move-In Baseline (record)</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    ) : (
                      <div
                        className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border min-h-[44px]"
                        data-testid="lease-condition-walkthrough-move-in-not-recorded"
                      >
                        <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">Move-In Baseline — not recorded</span>
                      </div>
                    )}
                    {moveOutWalkthroughSummary ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/leases/${leaseId}/condition-walkthroughs/move-out`)}
                        className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                        data-testid="lease-condition-walkthrough-move-out-link"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium text-foreground truncate">Move-Out Walkthrough (record)</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    ) : (
                      <div
                        className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border min-h-[44px]"
                        data-testid="lease-condition-walkthrough-move-out-not-recorded"
                      >
                        <ClipboardCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">Move-Out Walkthrough — not recorded</span>
                      </div>
                    )}
                    {deductionReportSummary ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/leases/${leaseId}/deduction-report`)}
                        className="flex items-center justify-between gap-2 p-3 rounded-lg border border-border hover:bg-accent transition-colors min-h-[44px] bg-transparent text-left"
                        data-testid="lease-deduction-report-link"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm font-medium text-foreground truncate">Deduction Report (record)</span>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </button>
                    ) : (
                      <div
                        className="flex items-center gap-2 p-3 rounded-lg border border-dashed border-border min-h-[44px]"
                        data-testid="lease-deduction-report-not-recorded"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-sm text-muted-foreground truncate">Deduction Report — not recorded</span>
                      </div>
                    )}
                  </div>
                </PageSection>
              )}

              {/* Dates */}
              <PageSection title="Dates">
                {fieldError && (editingField === 'startDate' || editingField === 'endDate') && (
                  <div className="mx-[var(--page-padding-x)] md:mx-4 mb-2 p-2 bg-destructive/5 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive m-0">{fieldError}</p>
                  </div>
                )}
                <DataGrid columns={lease.actualEndDate ? 3 : 2}>
                  {/* Start Date */}
                  {editingField === 'startDate' ? (
                    <div className="flex items-center gap-2 px-[var(--page-padding-x)] md:px-4 py-2" data-testid="start-date-edit-row">
                      <label className="text-sm text-muted-foreground shrink-0">Start</label>
                      <input
                        type="date"
                        autoFocus
                        data-testid="start-date-input"
                        value={editFieldValue}
                        onChange={(e) => setEditFieldValue(e.target.value)}
                        className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveField} disabled={isSavingField} data-testid="save-start-date-btn">
                        {isSavingField ? '...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEditField} disabled={isSavingField} data-testid="cancel-start-date-btn">
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <DataRow
                      label="Start"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          {formatLocalDate(lease.startDate)}
                          {cap.canEditDates && (
                            <button
                              type="button"
                              onClick={() => startEditField('startDate')}
                              className="text-xs text-primary hover:underline"
                              data-testid="edit-start-date-btn"
                            >
                              Edit
                            </button>
                          )}
                        </span>
                      }
                    />
                  )}

                  {/* End Date */}
                  {editingField === 'endDate' ? (
                    <div className="flex items-center gap-2 px-[var(--page-padding-x)] md:px-4 py-2" data-testid="end-date-edit-row">
                      <label className="text-sm text-muted-foreground shrink-0">End</label>
                      <input
                        type="date"
                        autoFocus
                        data-testid="end-date-input"
                        value={editFieldValue}
                        onChange={(e) => setEditFieldValue(e.target.value)}
                        className="px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveField} disabled={isSavingField} data-testid="save-end-date-btn">
                        {isSavingField ? '...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEditField} disabled={isSavingField} data-testid="cancel-end-date-btn">
                        Cancel
                      </Button>
                      {editFieldValue && (
                        <Button size="sm" variant="ghost" onClick={() => { setEditFieldValue(''); }} data-testid="clear-end-date-btn" className="text-xs text-muted-foreground">
                          Clear
                        </Button>
                      )}
                    </div>
                  ) : (
                    <DataRow
                      label="End"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          {formatLocalDate(lease.endDate)}
                          {cap.canEditDates && (
                            <button
                              type="button"
                              onClick={() => startEditField('endDate')}
                              className="text-xs text-primary hover:underline"
                              data-testid="edit-end-date-btn"
                            >
                              Edit
                            </button>
                          )}
                        </span>
                      }
                    />
                  )}

                  {lease.actualEndDate && (
                    <DataRow label="Actual End" value={formatLocalDate(lease.actualEndDate)} />
                  )}
                </DataGrid>
              </PageSection>

              {/* Financials */}
              <PageSection title="Financials">
                {fieldError && (
                  <div className="mx-[var(--page-padding-x)] md:mx-4 mb-2 p-2 bg-destructive/5 border border-destructive/20 rounded-md">
                    <p className="text-sm text-destructive m-0">{fieldError}</p>
                  </div>
                )}
                <DataGrid columns={2}>
                  {/* Monthly Rent — inline editable for BYO */}
                  {editingField === 'rent' ? (
                    <div className="flex items-center gap-2 px-[var(--page-padding-x)] md:px-4 py-2" data-testid="rent-edit-row">
                      <label className="text-sm text-muted-foreground shrink-0">Monthly Rent</label>
                      <span className="text-sm text-muted-foreground">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        autoFocus
                        data-testid="rent-amount-input"
                        value={editFieldValue}
                        onChange={(e) => setEditFieldValue(e.target.value)}
                        className="w-28 px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveField} disabled={isSavingField} data-testid="save-lease-terms-btn">
                        {isSavingField ? '...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEditField} disabled={isSavingField} data-testid="cancel-lease-terms-btn">
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <DataRow
                      label="Monthly Rent"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          {formatCurrency(lease.rentAmount)}
                          {cap.canEditTerms && (
                            <button
                              type="button"
                              onClick={() => startEditField('rent')}
                              className="text-xs text-primary hover:underline"
                              data-testid="edit-rent-btn"
                            >
                              Edit
                            </button>
                          )}
                        </span>
                      }
                    />
                  )}

                  {/* Security Deposit — inline editable for BYO */}
                  {editingField === 'deposit' ? (
                    <div className="flex items-center gap-2 px-[var(--page-padding-x)] md:px-4 py-2" data-testid="deposit-edit-row">
                      <label className="text-sm text-muted-foreground shrink-0">Security Deposit</label>
                      <span className="text-sm text-muted-foreground">$</span>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        autoFocus
                        data-testid="security-deposit-input"
                        value={editFieldValue}
                        onChange={(e) => setEditFieldValue(e.target.value)}
                        className="w-28 px-2 py-1 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                      <Button size="sm" variant="ghost" onClick={handleSaveField} disabled={isSavingField} data-testid="save-deposit-btn">
                        {isSavingField ? '...' : 'Save'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEditField} disabled={isSavingField} data-testid="cancel-deposit-btn">
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <DataRow
                      label="Security Deposit"
                      value={
                        <span className="inline-flex items-center gap-1.5">
                          {formatCurrency(lease.securityDeposit)}
                          {cap.canEditTerms && (
                            <button
                              type="button"
                              onClick={() => startEditField('deposit')}
                              className="text-xs text-primary hover:underline"
                              data-testid="edit-deposit-btn"
                            >
                              Edit
                            </button>
                          )}
                        </span>
                      }
                    />
                  )}

                  {/* Recurring Charges — opens modal for BYO */}
                  <DataRow
                    label="Recurring Charges"
                    value={
                      <span className="inline-flex items-center gap-1.5">
                        {chargeConfigs.length > 0
                          ? `${formatCurrency(recurringTotal)}/mo (${chargeConfigs.length})`
                          : 'None'}
                        {cap.canEditTerms && (
                          <button
                            type="button"
                            onClick={() => setIsChargesModalOpen(true)}
                            className="text-xs text-primary hover:underline"
                            data-testid="manage-charges-btn"
                          >
                            Manage
                          </button>
                        )}
                      </span>
                    }
                  />

                  <DataRow
                    label="Balance"
                    value={balance != null ? formatCurrency(balance) : '—'}
                  />

                  {/* Payment status — rendered for EVERY mode (AC-9) so an unconfigured active
                      lease reads as distinct from a paid-up one, not merely a zero balance. */}
                  <DataRow
                    label="Rent Collection"
                    data-testid="overview-payment-status"
                    value={
                      paymentConfig?.paymentMethod === 'ach' ? (
                        <span className="text-success font-medium">Collecting via ACH</span>
                      ) : paymentConfig?.paymentMethod === 'external' ? (
                        <span className="text-success font-medium">
                          Collecting via External (Zelle / Cash)
                        </span>
                      ) : (
                        <span className="text-warning font-medium">Rent collection not set up</span>
                      )
                    }
                  />
                </DataGrid>
              </PageSection>

              {/* Tenants */}
              <PageSection
                title="Tenants"
                data-testid="lease-tenants"
                action={cap.canEditTenants ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={openAddTenantModal}
                    data-testid="add-tenant-btn"
                  >
                    Add Tenant
                  </Button>
                ) : undefined}
              >
                {lease.tenants.length === 0 ? (
                  <p className="px-[var(--page-padding-x)] md:px-4 py-3 text-sm text-muted-foreground italic">
                    No tenants on this lease
                  </p>
                ) : (
                  lease.tenants.map((tenant) => (
                    <ListItem
                      key={tenant.id}
                      title={`${tenant.firstName} ${tenant.lastName}`}
                      subtitle={[tenant.email, tenant.phone].filter(Boolean).join(' · ')}
                      trailing={cap.canSetTenantDeparture ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDepartureModal(tenant)}
                          data-testid={`set-departure-date-btn-${tenant.id}`}
                        >
                          Set Departure Date
                        </Button>
                      ) : undefined}
                    />
                  ))
                )}
              </PageSection>

              {/* Current Period Payment Card — external payments */}
              {paymentConfig && paymentConfig.paymentMethod === 'external' && leaseId && (
                <div className="px-[var(--page-padding-x)] md:px-4 pb-4">
                  <CurrentPeriodPaymentCard
                    paymentConfig={paymentConfig}
                    matchingSummary={matchingSummary}
                  />
                </div>
              )}

              {/* Pending Late Fees — external payment leases */}
              {paymentConfig && paymentConfig.paymentMethod === 'external' && pendingLateFees.filter(f => f.status === 'pending').length > 0 && (
                <div className="px-[var(--page-padding-x)] md:px-4 pb-4">
                  <div className="bg-card rounded-lg border border-border p-4" data-testid="pending-late-fees-card">
                    <h4 className="text-sm font-semibold text-foreground m-0 mb-3">
                      Pending Late Fees
                      <span className="ml-2 text-xs font-medium text-warning bg-warning/10 border border-warning/30 px-1.5 py-0.5 rounded">
                        {pendingLateFees.filter(f => f.status === 'pending').length}
                      </span>
                    </h4>
                    {lateFeeError && (
                      <p className="text-sm text-destructive mb-3" role="alert">{lateFeeError}</p>
                    )}
                    <div className="flex flex-col gap-2">
                      {pendingLateFees.filter(f => f.status === 'pending').map(fee => (
                        <div
                          key={fee.id}
                          className="flex items-center justify-between rounded-md border border-border bg-background p-3"
                          data-testid={`pending-late-fee-${fee.id}`}
                        >
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground m-0">{fee.description}</p>
                            <p className="text-xs text-muted-foreground m-0">
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(fee.amount)}
                            </p>
                          </div>
                          <div className="flex gap-2 shrink-0 ml-3">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApprovePendingLateFee(fee.id)}
                              disabled={isApprovingFee === fee.id || isDismissingFee === fee.id}
                              data-testid={`approve-late-fee-${fee.id}`}
                            >
                              {isApprovingFee === fee.id ? 'Approving...' : 'Approve'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDismissPendingLateFee(fee.id)}
                              disabled={isApprovingFee === fee.id || isDismissingFee === fee.id}
                              data-testid={`dismiss-late-fee-${fee.id}`}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              {isDismissingFee === fee.id ? 'Dismissing...' : 'Dismiss'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ── Payments Tab ── */}
        <TabsContent value="payments" className="mt-0">
          <PaymentSettingsTab
            paymentConfig={paymentConfig}
            chargeConfigs={chargeConfigs}
            fundingSources={fundingSources}
            propertyBankInfo={propertyBankInfo}
            readiness={readiness}
            linkedBankAccounts={linkedBankAccounts}
            rentAmount={lease?.rentAmount ?? 0}
            onUpdateConfig={handleUpdateConfig}
            onMethodIntentChange={handleMethodIntentChange}
            onCreateCharge={handleCreateCharge}
            onUpdateCharge={handleUpdateCharge}
            onDeleteCharge={handleDeleteCharge}
            onSetPropertyDefault={handleSetPropertyDefault}
            onSetMonitoringAccount={handleSetMonitoringAccount}
            tenants={lease?.tenants ?? []}
            leaseId={leaseId ?? ''}
            focusSection={focusSection}
            onFocusConsumed={clearFocus}
            leaseState={lease?.state}
          />
        </TabsContent>

        {/* ── Transactions Tab ── */}
        <TabsContent value="transactions" className="mt-0">
          {leaseId && (
            <div className="px-[var(--page-padding-x)] md:px-0">
              {/* BR-LL-11 blocks WRITES on an archived lease, not the read view (prd.md
                  Event 3) — the ledger stays visible in read-only mode instead of being
                  hidden behind a notice. */}
              <LeaseLedgerTab
                leaseId={leaseId}
                onError={setError}
                onSuccess={setSuccessMessage}
                readOnly={!cap.canPostLedgerEntry}
              />
              {paymentConfig?.paymentMethod === 'external' && (
                <MatchingActivitySection leaseId={leaseId} />
              )}
            </div>
          )}
        </TabsContent>

        {/* ── Documents Tab ── */}
        <TabsContent value="documents" className="mt-0">
          {leaseId && unitId && cap.showDocuments && (
            <div className="px-[var(--page-padding-x)] md:px-0">
              <LeaseDocumentSection leaseId={leaseId} unitId={unitId} />
            </div>
          )}
          {/* Amendments are available on every lease — BYO leases have no LeaseDocument but
              still support add-only amendments (US-44, BR-LA-07). */}
          {leaseId && unitId && (
            <div className="px-[var(--page-padding-x)] md:px-0 mt-4">
              <LeaseAmendmentsSection leaseId={leaseId} unitId={unitId} />
            </div>
          )}
          {leaseId && (
            <DocumentsSection scope="LEASE" leaseId={leaseId} />
          )}
        </TabsContent>

        {/* ── Checklists Tab ── */}
        <TabsContent value="checklists" className="mt-0">
          {lease && leaseId && (
            <LeaseChecklistsTab
              leaseId={leaseId}
              archived={lease.state === 'archived'}
              startDate={lease.startDate}
              endDate={lease.endDate ?? null}
              checklists={checklists}
              onView={handleViewTenantChecklist}
            />
          )}
        </TabsContent>
      </DetailTabs>

      {viewingTenantChecklist && (
        <TenantChecklistModal
          walkthroughId={viewingTenantChecklist.walkthroughId}
          phase={viewingTenantChecklist.phase}
          onClose={() => setViewingTenantChecklist(null)}
        />
      )}

      {/* Edit Lease Modal */}
      {isEditModalOpen && lease && unitId && (
        <Modal
          isOpen={true}
          title="Edit Lease"
          onClose={() => {
            setIsEditModalOpen(false);
            setLeaseWarnings([]);
            setLeaseSubmitError(null);
          }}
        >
          <LeaseForm
            mode="edit"
            lease={lease}
            unitId={unitId}
            availableTenants={lease.tenants as unknown as TenantWithDetails[]}
            onSubmit={(data, acknowledgeWarnings) =>
              handleEditLease(data as UpdateLeaseInput, acknowledgeWarnings)
            }
            onCancel={() => {
              setIsEditModalOpen(false);
              setLeaseWarnings([]);
              setLeaseSubmitError(null);
            }}
            isSubmitting={isSavingLease}
            warnings={leaseWarnings}
            submitError={leaseSubmitError}
            onClearError={() => setLeaseSubmitError(null)}
          />
        </Modal>
      )}

      {/* Un-archive Confirmation Modal */}
      {isUnarchiveModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={() => setIsUnarchiveModalOpen(false)}
          data-testid="unarchive-confirm-modal"
        >
          <div
            className="bg-card rounded-lg w-[90%] max-w-md shadow-2xl p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground m-0">Un-archive this lease?</h3>
            <p className="text-sm text-muted-foreground m-0">
              This will clear the archive status. The lease will become <strong>Ended</strong> (not Active)
              because the end date remains set. Cancelled payment schedules will not be restarted.
            </p>
            <div className="flex justify-end gap-3 mt-2">
              <Button
                variant="outline"
                onClick={() => setIsUnarchiveModalOpen(false)}
                disabled={isUnarchiving}
                data-testid="unarchive-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUnarchive}
                disabled={isUnarchiving}
                data-testid="unarchive-confirm-btn"
              >
                {isUnarchiving ? 'Un-archiving...' : 'Un-archive'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tenant Modal */}
      {isAddTenantModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={closeAddTenantModal}
          data-testid="add-tenant-modal"
        >
          <div
            className="bg-card rounded-lg w-[90%] max-w-md shadow-2xl p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground m-0">Add Tenant to Lease</h3>

            {addTenantError && (
              <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive m-0">{addTenantError}</p>
              </div>
            )}

            {isLoadingAvailableTenants ? (
              <p className="text-sm text-muted-foreground">Loading tenants...</p>
            ) : availableTenantsToAdd.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No additional tenants available. Add tenants to this unit first from the Unit page.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                <p className="text-sm text-muted-foreground">Select a tenant to add to this lease:</p>
                <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
                  {availableTenantsToAdd.map((tenant) => (
                    <label
                      key={tenant.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedAddTenantId === tenant.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-accent'
                      }`}
                    >
                      <input
                        type="radio"
                        name="add-tenant-select"
                        value={tenant.id}
                        checked={selectedAddTenantId === tenant.id}
                        onChange={() => setSelectedAddTenantId(tenant.id)}
                        className="w-4 h-4 text-primary border-input"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {tenant.firstName && tenant.lastName
                            ? `${tenant.firstName} ${tenant.lastName}`
                            : tenant.email}
                        </div>
                        <div className="text-xs text-muted-foreground">{tenant.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 mt-2">
              <Button
                variant="outline"
                onClick={closeAddTenantModal}
                disabled={isAddingTenant}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddTenant}
                disabled={isAddingTenant || !selectedAddTenantId || isLoadingAvailableTenants}
                data-testid="add-tenant-confirm"
              >
                {isAddingTenant ? 'Adding...' : 'Add Tenant'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring Charges Modal (BYO leases — accessed from Financials section) */}
      {isChargesModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={() => setIsChargesModalOpen(false)}
          data-testid="recurring-charges-modal"
        >
          <div
            className="bg-card rounded-lg w-[90%] max-w-2xl shadow-2xl p-6 flex flex-col gap-4 max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground m-0">Manage Recurring Charges</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsChargesModalOpen(false)}
                data-testid="close-charges-modal-btn"
              >
                Close
              </Button>
            </div>
            <RecurringChargesSection
              chargeConfigs={chargeConfigs}
              onCreate={handleCreateCharge}
              onUpdate={handleUpdateCharge}
              onDelete={handleDeleteCharge}
            />
          </div>
        </div>
      )}

      {/* Departure Date Modal */}
      {departureTenant && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={closeDepartureModal}
          data-testid="departure-date-modal"
        >
          <div
            className="bg-card rounded-lg w-[90%] max-w-md shadow-2xl p-6 flex flex-col gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-foreground m-0">
              Set Departure Date for {departureTenant.firstName} {departureTenant.lastName}
            </h3>

            {lease && lease.tenants.length === 1 && (
              <div className="p-3 bg-warning/10 border border-warning/20 rounded-md">
                <p className="text-sm text-warning m-0">
                  This tenant is the only active member. Setting their departure date will leave the lease
                  with no tenants — consider archiving the lease instead.
                </p>
              </div>
            )}

            {departureError && (
              <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive m-0">{departureError}</p>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="departure-date-input" className="text-sm font-medium text-foreground">
                Departure Date
              </label>
              <input
                id="departure-date-input"
                type="date"
                data-testid="departure-date-input"
                value={departureDate}
                max={todayInTimezone(propertyTimezone)}
                onChange={(e) => setDepartureDate(e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground m-0">
                Date must not be in the future.
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-2">
              <Button
                variant="outline"
                onClick={closeDepartureModal}
                disabled={isDepartureSaving}
                data-testid="departure-date-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSetDeparture}
                disabled={isDepartureSaving || !departureDate}
                data-testid="departure-date-confirm"
              >
                {isDepartureSaving ? 'Saving...' : 'Confirm'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Schedule Move-Out Modal (BR-LL-08) */}
      <Modal
        isOpen={isScheduleMoveOutModalOpen}
        title="Schedule Move-Out"
        onClose={closeScheduleMoveOutModal}
        data-testid="schedule-move-out-modal"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground m-0">
            Setting a move-out date starts the lease's wind-down: automatic rent collection and
            charge projection stop as of that date, and the lease stays visible with its
            settlement tracked until you archive it.
          </p>

          {scheduleMoveOutError && (
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md" role="alert">
              <p className="text-sm text-destructive m-0">{scheduleMoveOutError}</p>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <label htmlFor="schedule-move-out-date-input" className="text-sm font-medium text-foreground">
              Move-Out Date
            </label>
            <input
              id="schedule-move-out-date-input"
              type="date"
              data-testid="schedule-move-out-date-input"
              value={scheduleMoveOutDate}
              min={lease ? lease.startDate.substring(0, 10) : undefined}
              onChange={(e) => setScheduleMoveOutDate(e.target.value)}
              aria-required="true"
              className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="schedule-move-out-reason-input" className="text-sm font-medium text-foreground">
              Reason (optional)
            </label>
            <textarea
              id="schedule-move-out-reason-input"
              data-testid="schedule-move-out-reason-input"
              value={scheduleMoveOutReason}
              onChange={(e) => setScheduleMoveOutReason(e.target.value)}
              maxLength={1000}
              rows={3}
              className="w-full px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              onClick={closeScheduleMoveOutModal}
              disabled={isSchedulingMoveOut}
              data-testid="schedule-move-out-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleScheduleMoveOut}
              disabled={isSchedulingMoveOut || !scheduleMoveOutDate}
              data-testid="schedule-move-out-confirm-btn"
            >
              {isSchedulingMoveOut ? 'Scheduling...' : 'Schedule Move-Out'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Share Tenant Access Link Modal (BR-8a/8b) */}
      <Modal
        isOpen={isShareLinkModalOpen}
        title="Share Tenant Access Link"
        onClose={closeShareLinkModal}
        data-testid="share-tenant-access-link-modal"
      >
        <div className="flex flex-col gap-4">
          {isIssuingShareLink && (
            <p className="text-sm text-muted-foreground m-0">Generating link…</p>
          )}

          {shareLinkError && (
            <div className="p-3 bg-destructive/5 border border-destructive/20 rounded-md" role="alert">
              <p className="text-sm text-destructive m-0">{shareLinkError}</p>
            </div>
          )}

          {shareLinkResult && (
            <>
              <p className="text-sm text-muted-foreground m-0">
                Share this link with the tenant to access their condition walkthrough:
              </p>
              <div className="p-3 bg-muted border border-border rounded-md break-all">
                <code className="text-xs font-mono text-foreground" data-testid="share-tenant-access-link-value">
                  {shareLinkResult.magicLinkUrl}
                </code>
              </div>
              <p className="text-xs text-muted-foreground m-0">
                Expires: {formatTimestamp(asIsoDateTimeString(shareLinkResult.expiresAt), propertyTimezone)}
              </p>
              {shareLinkCopyError && (
                <p className="text-xs text-destructive m-0" role="alert" data-testid="share-tenant-access-link-copy-error">
                  Couldn't copy automatically — select the link above and copy it manually.
                </p>
              )}
            </>
          )}

          {/* Footer actions are always rendered regardless of branch (loading/error/success)
              so a failed issue-token call still leaves the user an in-body way out, per FM5. */}
          <div className="flex gap-2 justify-end">
            {shareLinkResult && (
              <Button
                size="default"
                onClick={handleCopyShareLink}
                data-testid="copy-tenant-access-link-button"
              >
                {isShareLinkCopied ? 'Copied!' : 'Copy Link'}
              </Button>
            )}
            {shareLinkError && (
              <Button
                variant="outline"
                size="default"
                onClick={handleShareLinkClick}
                data-testid="retry-tenant-access-link-button"
              >
                Retry
              </Button>
            )}
            <Button
              variant="outline"
              size="default"
              onClick={closeShareLinkModal}
              data-testid="close-tenant-access-link-button"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive Outstanding-Balance Warning (ADR-LL-020) — warns, never blocks */}
      <Modal
        isOpen={isArchiveWarningOpen && !!lease}
        title={`${formatCurrency(balance ?? 0)} outstanding — archive anyway?`}
        onClose={() => setIsArchiveWarningOpen(false)}
        data-testid="archive-outstanding-balance-warning-modal"
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground m-0">
            The tenant still owes money on this lease. You can archive anyway if you&apos;re writing
            off the balance or collecting it outside Resida, but once it&apos;s archived no more
            charges, credits or payments can be recorded on it.
          </p>
          <div className="flex justify-end gap-3 mt-2">
            <Button
              variant="outline"
              onClick={() => setIsArchiveWarningOpen(false)}
              data-testid="archive-warning-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => { setIsArchiveWarningOpen(false); setIsArchiveModalOpen(true); }}
              data-testid="archive-warning-continue-btn"
              className="text-destructive hover:text-destructive"
            >
              Archive Anyway
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive Lease Modal */}
      {isArchiveModalOpen && lease && (
        <ArchiveLeaseModal
          lease={lease}
          onConfirm={handleArchive}
          onCancel={() => { setIsArchiveModalOpen(false); setArchiveError(null); }}
          isSubmitting={isArchiving}
          error={archiveError}
          onClearError={() => setArchiveError(null)}
        />
      )}
    </div>
    </PropertyTimezoneProvider>
  );
}

/* ── Loading Skeleton ── */

function LeaseDetailSkeleton() {
  return (
    <div className="max-w-4xl mx-auto pb-6" data-testid="lease-detail-page">
      <div className="px-[var(--page-padding-x)] pt-4">
        <Skeleton className="h-4 w-24 mb-2" />
        <Skeleton className="h-6 w-32" />
      </div>
      <div className="px-[var(--page-padding-x)] mt-4 flex gap-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-24 rounded-md" />
        ))}
      </div>
      <div className="px-[var(--page-padding-x)] mt-6 space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    </div>
  );
}

/* ── Payment Settings Tab ── */

interface PaymentSettingsTabProps {
  paymentConfig: PaymentConfig | null;
  chargeConfigs: LeaseChargeConfig[];
  fundingSources: LandlordFundingSource[];
  propertyBankInfo: PropertyBankInfo | null;
  readiness: PaymentReadiness;
  /** Lease's monthly rent (cents) — the status summary (AC-4) shows it; PaymentConfig has no rent field. */
  rentAmount: number;
  onUpdateConfig: (data: UpdatePaymentConfigInput) => Promise<boolean>;
  onMethodIntentChange: (method: MethodSelection) => Promise<boolean>;
  onCreateCharge: (data: CreateChargeConfigInput) => Promise<void>;
  onUpdateCharge: (chargeConfigId: string, data: UpdateChargeConfigInput) => Promise<void>;
  onDeleteCharge: (chargeConfigId: string) => void;
  onSetPropertyDefault: (fundingSourceId: string | null) => void;
  onSetMonitoringAccount: (linkedBankAccountId: string) => void;
  linkedBankAccounts: LinkedBankAccount[];
  tenants: Array<{ id: string; firstName: string; lastName: string; email: string; phone?: string | null }>;
  leaseId: string;
  /** Section to scroll to + highlight, from the ?focus= deep-link (deposit | terms | late-fees | monitoring | activate). */
  focusSection?: string | null;
  /** Called once the focus has been handled, so the page can strip ?focus= and allow re-triggering. */
  onFocusConsumed?: () => void;
  /** Drives the status summary's collection-state text (Mode B only) — a lease past `active`
   *  no longer has automatic collection running (BR-LL-10/BR-LL-11) and must say so. */
  leaseState?: LeaseState;
}

/**
 * Thin dispatcher between the two state-aware Payments-tab layouts (change-19): a linear setup
 * checklist while payments are unconfigured (Mode A), or a status-led summary with collapsible
 * editors once they are (Mode B). Owns the method-selection state (selectedMethod) and the
 * ?focus= deep-link scroll/highlight effect — both modes' expand-in-place behavior derives from
 * the SAME `focusSection` prop this effect reads, so a target section is already expanded by the
 * time this effect's DOM-query retry loop looks for it.
 */
function PaymentSettingsTab({
  paymentConfig,
  chargeConfigs,
  fundingSources,
  propertyBankInfo,
  readiness,
  linkedBankAccounts,
  rentAmount,
  onUpdateConfig,
  onMethodIntentChange,
  onCreateCharge,
  onUpdateCharge,
  onDeleteCharge,
  onSetPropertyDefault,
  onSetMonitoringAccount,
  tenants,
  leaseId,
  focusSection,
  onFocusConsumed,
  leaseState,
}: PaymentSettingsTabProps) {
  const [selectedMethod, setSelectedMethod] = useState<MethodSelection>(
    () => deriveInitialMethodIntent(paymentConfig, propertyBankInfo)
  );

  const isEnabled = (paymentConfig?.paymentMethod ?? 'none') !== 'none';

  // Deep-link focus: scroll the targeted section into view and pulse a highlight ring so the
  // landlord can tell which item the workflow CTA sent them to. The section may not be in the DOM
  // yet on first paint (config still loading / method just selected), so retry briefly.
  useEffect(() => {
    if (!focusSection) return;
    const testidByFocus: Record<string, string> = {
      deposit: 'ach-deposit-account-section',
      monitoring: 'external-monitoring-account-section',
      terms: 'payment-terms-card',
      'late-fees': 'late-fee-config-form',
      activate: 'payment-activation-card',
    };
    const tid = testidByFocus[focusSection];
    if (!tid) return;

    let cancelled = false;
    let removeTimer: ReturnType<typeof setTimeout> | undefined;
    const tryFocus = (attempt: number) => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(`[data-testid="${tid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Remove → force reflow → re-add so the CSS pulse restarts every time, even when the same
        // section is focused again (re-adding a class the element already has won't restart it).
        el.classList.remove('workflow-focus-section');
        void el.offsetWidth;
        el.classList.add('workflow-focus-section');
        removeTimer = setTimeout(() => el.classList.remove('workflow-focus-section'), 2600);
        // Consume the focus param so a repeat press of the same CTA re-navigates (null → value)
        // and re-fires this effect — otherwise the identical-URL nav is a no-op.
        onFocusConsumed?.();
      } else if (attempt < 16) {
        setTimeout(() => tryFocus(attempt + 1), 200);
      }
    };
    const startTimer = setTimeout(() => tryFocus(0), 150);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      if (removeTimer) clearTimeout(removeTimer);
    };
    // selectedMethod is a dependency because the deposit/terms/late-fees sections are gated behind
    // selectedMethod !== 'none'. When config loads after first paint, selectedMethod transitions
    // none → ach/external and those sections mount; re-running the effect then lets the retry find a
    // section that wasn't in the DOM during the initial budget (otherwise focus is silently lost and
    // ?focus= is left stranded, making a repeat CTA press a no-op).
  }, [focusSection, onFocusConsumed, selectedMethod]);

  // Optimistic method switch, rolled back on a failed persist (AC-10) — the failure itself is
  // surfaced by handleMethodIntentChange via the page's existing error alert (setError).
  const handleMethodChange = async (method: MethodSelection) => {
    const previous = selectedMethod;
    setSelectedMethod(method);
    const ok = await onMethodIntentChange(method);
    if (!ok) setSelectedMethod(previous);
  };

  return isEnabled ? (
    <PaymentsStatusPanel
      paymentConfig={paymentConfig}
      chargeConfigs={chargeConfigs}
      readiness={readiness}
      fundingSources={fundingSources}
      propertyBankInfo={propertyBankInfo}
      linkedBankAccounts={linkedBankAccounts}
      onUpdateConfig={onUpdateConfig}
      onMethodChange={handleMethodChange}
      onCreateCharge={onCreateCharge}
      onUpdateCharge={onUpdateCharge}
      onDeleteCharge={onDeleteCharge}
      onSetPropertyDefault={onSetPropertyDefault}
      onSetMonitoringAccount={onSetMonitoringAccount}
      focusSection={focusSection}
      selectedMethod={selectedMethod}
      rentAmount={rentAmount}
      tenants={tenants}
      leaseId={leaseId}
      leaseState={leaseState}
    />
  ) : (
    <PaymentsSetupChecklist
      paymentConfig={paymentConfig}
      selectedMethod={selectedMethod}
      readiness={readiness}
      fundingSources={fundingSources}
      propertyBankInfo={propertyBankInfo}
      linkedBankAccounts={linkedBankAccounts}
      onUpdateConfig={onUpdateConfig}
      onMethodChange={handleMethodChange}
      onSetPropertyDefault={onSetPropertyDefault}
      onSetMonitoringAccount={onSetMonitoringAccount}
      focusSection={focusSection}
    />
  );
}

export default LeaseDetailPage;
